import React, { useState, useMemo, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const ALL_MONTHS = ["1月","2月","3月","4月","5月","6月","7月","8月","9月","10月","11月","12月"];
const toN = (v) => v===""||v==null ? 0 : Number(String(v).replace(/[^0-9-]/g,""))||0;
const parseTSV = (text) => text.trim().split(/\r?\n/).map(r=>r.split("\t").map(c=>c.trim()));
const getMonths = (sm) => [...ALL_MONTHS.slice(sm-1), ...ALL_MONTHS.slice(0,sm-1)];
const mkMap = (months, val=0) => Object.fromEntries(months.map(m=>[m,val]));
const makeId = (prefix="id") => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2,10)}`;
};
const USER_OPTIONS = ["木原", "杉本"];
function getMonthStatusLabel(hasRev, hasExp) {
  if (!hasRev && !hasExp) return { label:"未入力", bg:"#F3F4F6", color:"#666" };
  if (hasRev && hasExp) return { label:"入力済み", bg:"#E8F4E8", color:"#2D7A2D" };
  return { label:"一部入力", bg:"#FFF3DC", color:"#AA7722" };
}

const normalizeKey = (s) => String(s ?? "").replace(/[\s　]+/g, "").trim().toLowerCase();
const isMonthLike = (s) => /^(\d{1,2})月$/.test(String(s ?? "").trim()) || /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)$/i.test(String(s ?? "").trim());
const findIndexByName = (rows, name, fallbackIndex = -1) => {
  const key = normalizeKey(name);
  if (key) {
    const hit = rows.findIndex(r => normalizeKey(r.name) === key);
    if (hit >= 0) return hit;
  }
  return fallbackIndex >= 0 && fallbackIndex < rows.length ? fallbackIndex : -1;
};

const DEFAULT_EXPENSE_ITEMS = [
  {id:"officer",   label:"役員報酬", auto:false },
  {id:"salary",    label:"給料",     auto:true,  autoKey:"salary" },
  {id:"bonus",     label:"賞与",     auto:true,  autoKey:"bonus"  },
  {id:"transport", label:"交通費",   auto:true,  autoKey:"transport"},
  {id:"outsource", label:"外注費",   auto:false},
  {id:"office",    label:"事務所",   auto:false},
  {id:"insurance", label:"社会保険", auto:false},
  {id:"entertain", label:"交際費",   auto:false},
  {id:"other",     label:"その他経費",auto:false},
];

function makeInitial(months) {
  return {
    revenue:[
      {id:1, mode:"direct", name:"売上A", monthly:mkMap(months,2000000)},
      {id:2, mode:"direct", name:"売上B", monthly:mkMap(months,1000000)},
    ],
    staff:[
      {id:10, name:"木原", salary:mkMap(months,650000), bonus:mkMap(months,0), transport:mkMap(months,10000)},
      {id:11, name:"杉本", salary:mkMap(months,650000), bonus:mkMap(months,0), transport:mkMap(months,10000)},
    ],
    expenses: Object.fromEntries(DEFAULT_EXPENSE_ITEMS.map(e=>[e.id, mkMap(months, e.id==="office"?50000:e.id==="insurance"?120000:0)])),
    actuals:  {revItems:{}, items: Object.fromEntries(DEFAULT_EXPENSE_ITEMS.map(e=>[e.id, mkMap(months,"")])), simple:{revItems:[{id:makeId("simpleRev"), name:"売上合計", monthly:mkMap(months,"")}], expItems:[{id:makeId("simpleExp"), name:"費用合計", monthly:mkMap(months,"")}], rev:mkMap(months,""), exp:mkMap(months,"")}},
  };
}


function normalizeSimpleActuals(simple, months) {
  const src = simple || {};
  const migratedRevItems = src.revItems && src.revItems.length
    ? src.revItems.map(item => ({
        id: item.id || makeId("simpleRev"),
        name: item.name || "売上項目",
        monthly: Object.fromEntries(months.map(m => [m, item.monthly?.[m] ?? ""])),
      }))
    : [{ id: makeId("simpleRev"), name: "売上合計", monthly: Object.fromEntries(months.map(m => [m, src.rev?.[m] ?? ""])) }];
  const migratedExpItems = src.expItems && src.expItems.length
    ? src.expItems.map(item => ({
        id: item.id || makeId("simpleExp"),
        name: item.name || "費用項目",
        monthly: Object.fromEntries(months.map(m => [m, item.monthly?.[m] ?? ""])),
      }))
    : [{ id: makeId("simpleExp"), name: "費用合計", monthly: Object.fromEntries(months.map(m => [m, src.exp?.[m] ?? ""])) }];

  const rev = Object.fromEntries(months.map(m => [m, migratedRevItems.reduce((s, item) => s + toN(item.monthly?.[m]), 0) ? String(migratedRevItems.reduce((s, item) => s + toN(item.monthly?.[m]), 0)) : migratedRevItems.some(item => (item.monthly?.[m] ?? "") !== "") ? String(migratedRevItems.reduce((s, item) => s + toN(item.monthly?.[m]), 0)) : ""]));
  const exp = Object.fromEntries(months.map(m => [m, migratedExpItems.reduce((s, item) => s + toN(item.monthly?.[m]), 0) ? String(migratedExpItems.reduce((s, item) => s + toN(item.monthly?.[m]), 0)) : migratedExpItems.some(item => (item.monthly?.[m] ?? "") !== "") ? String(migratedExpItems.reduce((s, item) => s + toN(item.monthly?.[m]), 0)) : ""]));

  return { ...src, revItems: migratedRevItems, expItems: migratedExpItems, rev, exp };
}

function fmtDiff(diff, positiveIsGood = true) {
  if (diff == null) return null;
  const good = positiveIsGood ? diff >= 0 : diff <= 0;
  const sign = diff >= 0 ? "+" : "−";
  return { text: `${sign}${Math.abs(diff).toLocaleString()}`, color: good ? "#2D7A2D" : "#C0392B" };
}



// ─── SHARED UI ────────────────────────────────────────────────

const TAB_COLOR = {
  dashboard:"#2D7A2D", revenue:"#2255AA", expenses:"#C0392B",
  salary:"#AA7722", transport:"#AA7722", actuals:"#553399", settings:"#666",
};

function NavTab({label, active, onClick, badge, tabId}) {
  const accent = TAB_COLOR[tabId] || "#2D7A2D";
  return (
    <button onClick={onClick} style={{
      padding:"10px 18px",
      background:active?`${accent}18`:"transparent",
      border:"none",
      borderBottom:active?`3px solid ${accent}`:"3px solid transparent",
      color:active?accent:"#999",
      fontWeight:active?"800":"400",
      cursor:"pointer", fontSize:"13px", fontFamily:"'Noto Sans JP',sans-serif",
      display:"flex", alignItems:"center", gap:"6px", whiteSpace:"nowrap",
      transition:"all 0.15s",
    }}>
      {label}
      {badge && <span style={{background:accent,color:"#fff",borderRadius:"999px",fontSize:"10px",padding:"1px 7px",fontWeight:"700"}}>{badge}</span>}
    </button>
  );
}

function DropdownNavTab({label, active, subActive, onClick, onSubClick, subItems, badge, tabId}) {
  const [hover, setHover] = useState(false);
  const isAnyActive = active || !!subActive;
  const accent = TAB_COLOR[tabId] || TAB_COLOR[subActive] || "#C0392B";
  return (
    <div style={{position:"relative"}} onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}>
      <button onClick={onClick} style={{
        padding:"10px 18px",
        background:isAnyActive?`${accent}18`:"transparent",
        border:"none",
        borderBottom:isAnyActive?`3px solid ${accent}`:"3px solid transparent",
        color:isAnyActive?accent:"#999",
        fontWeight:isAnyActive?"800":"400",
        cursor:"pointer", fontSize:"13px", fontFamily:"'Noto Sans JP',sans-serif",
        display:"flex", alignItems:"center", gap:"4px", whiteSpace:"nowrap",
        transition:"all 0.15s",
      }}>
        {label}
        {badge && <span style={{background:accent,color:"#fff",borderRadius:"999px",fontSize:"10px",padding:"1px 7px",fontWeight:"700"}}>{badge}</span>}
        <span style={{fontSize:"9px",color:isAnyActive?accent:"#ccc",marginLeft:"2px"}}>▾</span>
      </button>
      {hover && (
        <div style={{
          position:"absolute",top:"100%",left:0,zIndex:200,
          background:"#fff",border:`1px solid ${accent}44`,borderRadius:"8px",
          boxShadow:"0 6px 20px rgba(0,0,0,0.12)",minWidth:"140px",padding:"4px 0",
        }}>
          {subItems.map(item=>{
            const subAccent = TAB_COLOR[item.id] || accent;
            return (
              <button key={item.id} onClick={()=>{onSubClick(item.id);setHover(false);}} style={{
                display:"block",width:"100%",textAlign:"left",
                padding:"9px 16px",background:subActive===item.id?`${subAccent}14`:"transparent",
                border:"none",color:subActive===item.id?subAccent:"#555",
                fontWeight:subActive===item.id?"700":"400",
                cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",
                whiteSpace:"nowrap",
              }}>
                {subActive===item.id && <span style={{marginRight:"6px",fontSize:"10px"}}>●</span>}
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Card({children, style}) {
  return <div style={{background:"#fff",borderRadius:"8px",border:"1px solid #E8EDE8",padding:"20px",...style}}>{children}</div>;
}

function ST({children}) {
  return <div style={{fontSize:"11px",fontWeight:"700",color:"#2D7A2D",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>{children}</div>;
}

function NI({value, onChange, style, w="88px"}) {
  const isEmpty = value===""||value==null;
  return (
    <input type="text"
      value={isEmpty ? "" : String(value)}
      placeholder="—"
      onChange={e=>onChange(e.target.value.replace(/[^0-9]/g,""))}
      style={{
        width:w, padding:"4px 5px", border:"1px solid #D0D8D0", borderRadius:"4px",
        textAlign:"right", fontSize:"12px", fontFamily:"monospace",
        background: isEmpty ? "#FAFCFA" : "#fff",
        color: (value===0||value==="0") ? "#bbb" : "inherit",
        ...style
      }}
    />
  );
}

function BulkBtn({onSet}) {
  const [open,setOpen] = useState(false);
  const [val,setVal]   = useState("");
  if (!open) return (
    <button onClick={()=>setOpen(true)} style={{fontSize:"10px",padding:"3px 6px",background:"#E8F4E8",border:"1px solid #C8E6C8",borderRadius:"3px",cursor:"pointer",color:"#2D7A2D",whiteSpace:"nowrap"}}>一括</button>
  );
  return (
    <div style={{display:"flex",gap:"2px",alignItems:"center"}}>
      <input autoFocus type="text" value={val} onChange={e=>setVal(e.target.value.replace(/[^0-9]/g,""))}
        style={{width:"58px",fontSize:"11px",padding:"2px 4px",border:"1px solid #2D7A2D",borderRadius:"3px"}}
        onKeyDown={e=>{if(e.key==="Enter"){onSet(val);setOpen(false);setVal("");}if(e.key==="Escape")setOpen(false);}}
      />
      <button onClick={()=>{onSet(val);setOpen(false);setVal("");}}
        style={{fontSize:"10px",padding:"3px 4px",background:"#2D7A2D",color:"#fff",border:"none",borderRadius:"3px",cursor:"pointer"}}>✓</button>
    </div>
  );
}



// ─── PASTE PANEL ──────────────────────────────────────────────

function PastePanel({mode, onApply, months}) {
  const [text,setText]       = useState("");
  const [preview,setPreview] = useState(null);
  const [error,setError]     = useState("");
  const hints = {
    revenue_row:"「案件名|5月|6月|…」の行をコピペ（名前列なしもOK）",
    expense_row:"「科目名|5月|6月|…」の行をコピペ",
    actuals_col:"「売上|経費」の2列×12行、または「売上12ヶ月行 + 経費12ヶ月行」の2行形式をコピペ",
    person_row: "「氏名|5月|6月|…」の行をコピペ",
  };
  const mc = months.length;
  const parse = () => {
    setError("");
    try {
      const rows = parseTSV(text).filter(r=>r.some(c=>c!==""));
      if (!rows.length) { setError("データが空です"); return; }
      if (mode !== "actuals_col") {
        const parsed = rows.filter(r=>r.some(c=>c!=="")).map(r => {
          let label = "";
          let nums = r;
          if (r.length === mc + 1) {
            label = r[0];
            nums = r.slice(1);
          } else if (r.length === mc) {
            nums = r;
          } else if (r.length > mc + 1) {
            label = r[0];
            nums = r.slice(1, mc + 1);
          } else {
            nums = [...r, ...Array(mc).fill("0")].slice(0, mc);
            label = "";
          }
          return {name:label, label, values:nums.slice(0,mc).map(toN)};
        });
        setPreview({type:mode, rows:parsed});
      } else {
        if (rows.length === 2 && rows[0].length >= mc) {
          const revRow = rows[0];
          const expRow = rows[1];
          const revStart = isNaN(toN(revRow[0])) || isMonthLike(revRow[0]) ? 1 : 0;
          const expStart = isNaN(toN(expRow[0])) || isMonthLike(expRow[0]) ? 1 : 0;
          const dr = months.map((_, i) => ({
            rev: toN(revRow[revStart + i] ?? 0),
            exp: toN(expRow[expStart + i] ?? 0),
          }));
          setPreview({type:"actuals_col", rows:dr});
        } else {
          const dr = rows.map(r => {
            let cells = r;
            if (r.length >= 3 && isMonthLike(r[0])) {
              cells = r.slice(1);
            } else if (r.length > 2) {
              cells = r.slice(-2);
            }
            if (cells.length < 2) throw new Error("2列必要");
            return {rev:toN(cells[0]), exp:toN(cells[1])};
          });
          if (dr.length < mc) throw new Error(`${mc}行必要（${dr.length}行）`);
          setPreview({type:"actuals_col", rows:dr.slice(0,mc)});
        }
      }
    } catch(e) { setError(e.message); }
  };
  const apply = () => { if(!preview)return; onApply(preview); setText(""); setPreview(null); };
  return (
    <div style={{background:"#F5FAF5",border:"1px dashed #A8D5A8",borderRadius:"8px",padding:"14px",marginBottom:"16px"}}>
      <div style={{fontSize:"11px",fontWeight:"700",color:"#2D7A2D",marginBottom:"4px"}}>📋 Excelから一括貼り付け</div>
      <div style={{fontSize:"11px",color:"#666",marginBottom:"8px",fontFamily:"'Noto Sans JP',sans-serif"}}>{hints[mode]}</div>
      <textarea value={text} onChange={e=>{setText(e.target.value);setPreview(null);setError("");}}
        placeholder="ここにExcelのセルをコピー＆ペースト"
        style={{width:"100%",height:"58px",fontSize:"12px",fontFamily:"monospace",border:"1px solid #C8E6C8",borderRadius:"4px",padding:"6px",resize:"vertical",background:"#fff",boxSizing:"border-box"}}
      />
      <div style={{display:"flex",gap:"8px",marginTop:"6px",alignItems:"center"}}>
        <button onClick={parse} disabled={!text.trim()}
          style={{padding:"5px 12px",background:text.trim()?"#2D7A2D":"#ccc",color:"#fff",border:"none",borderRadius:"4px",cursor:text.trim()?"pointer":"default",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>
          プレビュー
        </button>
        {preview && (
          <button onClick={apply}
            style={{padding:"5px 12px",background:"#1A5C1A",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",fontWeight:"700"}}>
            ✓ 適用
          </button>
        )}
        {error && <span style={{color:"#C0392B",fontSize:"11px"}}>⚠ {error}</span>}
      </div>
      {preview && mode!=="actuals_col" && (
        <div style={{marginTop:"10px",overflowX:"auto"}}>
          <div style={{fontSize:"10px",color:"#2D7A2D",marginBottom:"4px",fontWeight:"700"}}>プレビュー（適用するまで反映されません）</div>
          <table style={{borderCollapse:"collapse",fontSize:"11px",fontFamily:"monospace"}}>
            <thead><tr>
              <th style={{padding:"2px 5px",background:"#E8F4E8",border:"1px solid #C8E6C8"}}>名前</th>
              {months.map(m=><th key={m} style={{padding:"2px 5px",background:"#E8F4E8",border:"1px solid #C8E6C8"}}>{m}</th>)}
            </tr></thead>
            <tbody>
              {preview.rows.map((r,i)=>(
                <tr key={i}>
                  <td style={{padding:"2px 5px",border:"1px solid #E0ECE0",color:"#2D7A2D",fontWeight:"700"}}>{r.name||`行${i+1}`}</td>
                  {r.values.map((v,j)=><td key={j} style={{padding:"2px 5px",border:"1px solid #E0ECE0",textAlign:"right"}}>{v.toLocaleString()}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── REVENUE TABLE BLOCK (外出し) ─────────────────────────────

function RevenueTableBlock({items, title, badge, blue, months, onUpdateMonthly, onUpdateName, onRemove}) {
  const [editName, setEditName] = useState(null);
  if (items.length === 0) return null;
  return (
    <div style={{marginBottom:"18px"}}>
      <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"8px"}}>
        <span style={{fontSize:"12px",fontWeight:"700",color:"#444",fontFamily:"'Noto Sans JP',sans-serif"}}>{title}</span>
        <span style={{fontSize:"10px",padding:"2px 8px",background:blue?"#EEF5FF":"#E8F4E8",color:blue?"#2255AA":"#2D7A2D",border:`1px solid ${blue?"#AAC4EE":"#C8E6C8"}`,borderRadius:"999px"}}>{badge}</span>
      </div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
          <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}>
            <th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"80px"}}>名前</th>
            {months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 2px",color:"#888",minWidth:"66px"}}>{m}</th>)}
            <th style={{textAlign:"right",padding:"5px 8px",color:"#888",minWidth:"72px",position:"sticky",right:"26px",background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>合計</th>
            <th style={{width:"26px",position:"sticky",right:0,background:"#fff"}}></th>
          </tr></thead>
          <tbody>
            {items.map((r,ri)=>(
              <tr key={r.id} style={{borderBottom:"1px solid #F0F4F0",background:ri%2===0?"#FAFCFA":"#fff"}}>
                <td style={{padding:"3px 8px"}}>
                  {editName===r.id
                    ? <input autoFocus value={r.name} onChange={e=>onUpdateName(r.id,e.target.value)} onBlur={()=>setEditName(null)} style={{width:"72px",border:"1px solid #2D7A2D",borderRadius:"3px",padding:"2px 4px",fontSize:"12px"}}/>
                    : <span onClick={()=>setEditName(r.id)} style={{cursor:"pointer",color:blue?"#2255AA":"#2D7A2D",textDecoration:"underline dotted",fontFamily:"'Noto Sans JP',sans-serif"}}>{r.name}</span>
                  }
                </td>
                {months.map(m=>(
                  <td key={m} style={{padding:"3px 1px",textAlign:"right"}}>
                    <NI value={r.monthly[m]} onChange={val=>onUpdateMonthly(r.id,m,val)} w="64px"/>
                  </td>
                ))}
                <td style={{padding:"3px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#1A3A1A",position:"sticky",right:"26px",background:ri%2===0?"#FAFCFA":"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>
                  {months.reduce((s,m)=>s+toN(r.monthly[m]),0).toLocaleString()}
                </td>
                <td style={{position:"sticky",right:0,background:ri%2===0?"#FAFCFA":"#fff"}}><button onClick={()=>onRemove(r.id)} style={{background:"none",border:"none",color:"#C0392B",cursor:"pointer",fontSize:"14px"}}>×</button></td>
              </tr>
            ))}
            <tr style={{borderTop:"2px solid #E8EDE8",background:"#F5FAF5"}}>
              <td style={{padding:"5px 8px",fontWeight:"700",color:blue?"#2255AA":"#2D7A2D",fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>小計</td>
              {months.map(m=><td key={m} style={{padding:"5px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#1A3A1A",fontSize:"12px"}}>{items.reduce((s,r)=>s+toN(r.monthly[m]),0).toLocaleString()}</td>)}
              <td style={{padding:"5px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:blue?"#2255AA":"#2D7A2D",fontSize:"13px",position:"sticky",right:"26px",background:"#F5FAF5",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{items.reduce((s,r)=>s+months.reduce((ss,m)=>ss+toN(r.monthly[m]),0),0).toLocaleString()}</td>
              <td style={{position:"sticky",right:0,background:"#F5FAF5"}}></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── SETTINGS ─────────────────────────────────────────────────

function Settings({startMonth, onChangeStartMonth, companyName, onChangeCompanyName}) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      <Card>
        <ST>会社名設定</ST>
        <div style={{fontSize:"12px",color:"#555",marginBottom:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>右上に表示される会社名を設定します</div>
        <input
          value={companyName}
          onChange={e=>onChangeCompanyName(e.target.value)}
          placeholder="会社名を入力"
          style={{fontSize:"14px",fontWeight:"700",border:"1px solid #D0D8D0",borderRadius:"6px",padding:"8px 12px",width:"240px",fontFamily:"'Noto Sans JP',sans-serif",color:"#1A3A1A"}}
        />
      </Card>
      <Card>
        <ST>会計期間設定</ST>
        <div style={{fontSize:"12px",color:"#555",marginBottom:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>決算月を選択してください（翌月が期首になります）</div>
        <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
          {ALL_MONTHS.map((m,i)=>{
            const start = (i+1)%12+1;
            const isActive = startMonth===start;
            return (
              <button key={m} onClick={()=>onChangeStartMonth(start)}
                style={{padding:"7px 14px",background:isActive?"#2D7A2D":"#F0F4F0",color:isActive?"#fff":"#555",border:isActive?"1px solid #2D7A2D":"1px solid #D8E0D8",borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",fontWeight:isActive?"700":"400"}}>
                {m}決算
              </button>
            );
          })}
        </div>
        <div style={{marginTop:"16px",padding:"12px",background:"#F5FAF5",borderRadius:"6px",fontSize:"12px",color:"#555",fontFamily:"'Noto Sans JP',sans-serif"}}>
          現在の設定：<strong style={{color:"#2D7A2D"}}>{ALL_MONTHS[(startMonth+10)%12]}決算</strong>（期首：{ALL_MONTHS[startMonth-1]}）
        </div>
      </Card>
    </div>
  );
}


// ─── REVENUE EDITOR ───────────────────────────────────────────

function RevenueEditor({revenue, setRevenue, months}) {
  const [showPasteA, setShowPasteA] = useState(false);
  const [showPasteB, setShowPasteB] = useState(false);
  const applyPasteA = (preview) => {
    const ni = preview.rows.map((r,i)=>({id:makeId("rev"), mode:"direct", name:r.name||`売上${String.fromCharCode(65+revenue.filter(x=>x.mode==="direct").length+i)}`, monthly:Object.fromEntries(months.map((m,j)=>[m,r.values[j]]))}));
    setRevenue(prev=>[...prev,...ni]);
    setShowPasteA(false);
  };
  const applyPasteB = (preview) => {
    const ni = preview.rows.map((r,i)=>({id:makeId("rev"), mode:"ses", name:r.name||`要員${revenue.filter(x=>x.mode==="ses").length+i+1}`, monthly:Object.fromEntries(months.map((m,j)=>[m,r.values[j]]))}));
    setRevenue(prev=>[...prev,...ni]);
    setShowPasteB(false);
  };
  const addDirect = () => setRevenue(prev=>[...prev,{id:makeId("rev") ,mode:"direct",name:`売上${String.fromCharCode(65+prev.length)}`,monthly:mkMap(months,0)}]);
  const addSES    = () => setRevenue(prev=>[...prev,{id:makeId("rev") ,mode:"ses",name:`要員${prev.length+1}`,monthly:mkMap(months,0)}]);
  const remove    = (id) => setRevenue(prev=>prev.filter(x=>x.id!==id));
  const updMonthly = (id,m,val) => setRevenue(prev=>prev.map(x=>x.id===id?{...x,monthly:{...x.monthly,[m]:val}}:x));
  const updName    = (id,name)  => setRevenue(prev=>prev.map(x=>x.id===id?{...x,name}:x));
  const direct = revenue.filter(r=>r.mode==="direct");
  const ses    = revenue.filter(r=>r.mode==="ses");
  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      <Card>
        <ST>収入明細</ST>
        <div style={{display:"flex",gap:"8px",justifyContent:"flex-end",marginBottom:"16px"}}>
          <button onClick={()=>setShowPasteA(v=>!v)} style={{padding:"6px 14px",background:showPasteA?"#E8F4E8":"#F0F4F0",color:showPasteA?"#2D7A2D":"#555",border:`1px solid ${showPasteA?"#2D7A2D":"#D0D8D0"}`,borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>📋 A 貼り付け</button>
          <button onClick={addDirect} style={{padding:"6px 14px",background:"#2D7A2D",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>＋ 売上直入力</button>
        </div>
        {showPasteA && <div style={{marginBottom:"16px"}}><PastePanel mode="revenue_row" onApply={applyPasteA} months={months}/></div>}
        <RevenueTableBlock items={direct} title="A. 売上直入力" badge="案件・契約単位" months={months} onUpdateMonthly={updMonthly} onUpdateName={updName} onRemove={remove}/>
        <div style={{display:"flex",gap:"8px",justifyContent:"flex-end",margin:"8px 0 12px"}}>
          <button onClick={()=>setShowPasteB(v=>!v)} style={{padding:"6px 14px",background:showPasteB?"#E8EEFF":"#F0F4F0",color:showPasteB?"#2255AA":"#555",border:`1px solid ${showPasteB?"#2255AA":"#D0D8D0"}`,borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>📋 B 貼り付け</button>
          <button onClick={addSES} style={{padding:"6px 14px",background:"#2255AA",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>＋ 人別売上</button>
        </div>
        {showPasteB && <div style={{marginBottom:"16px"}}><PastePanel mode="person_row" onApply={applyPasteB} months={months}/></div>}
        <RevenueTableBlock items={ses} title="B. 人別売上" badge="要員単位" blue months={months} onUpdateMonthly={updMonthly} onUpdateName={updName} onRemove={remove}/>
        <div style={{borderTop:"2px solid #2D7A2D",paddingTop:"10px",overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
            <tbody><tr style={{background:"#E8F4E8"}}>
              <td style={{padding:"6px 8px",fontWeight:"800",color:"#1A3A1A",fontFamily:"'Noto Sans JP',sans-serif",minWidth:"80px"}}>月次総売上</td>
              {months.map(m=><td key={m} style={{padding:"6px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:"#1A3A1A",fontSize:"13px",minWidth:"66px"}}>{revenue.reduce((s,r)=>s+toN(r.monthly[m]),0).toLocaleString()}</td>)}
              <td style={{padding:"6px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:"#2D7A2D",fontSize:"14px",minWidth:"72px",position:"sticky",right:"26px",background:"#E8F4E8",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{revenue.reduce((s,r)=>s+months.reduce((ss,m)=>ss+toN(r.monthly[m]),0),0).toLocaleString()}</td>
              <td style={{width:"26px",position:"sticky",right:0,background:"#E8F4E8"}}></td>
            </tr></tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── SALARY SECTION TABLE (外出し) ───────────────────────────

function SalarySectionTable({field, label, color, staff, setStaff, months}) {
  const [editName, setEditName] = useState(null);
  const remove = (id) => setStaff(prev=>prev.filter(s=>s.id!==id));
  const upd    = (id,m,val) => setStaff(prev=>prev.map(s=>s.id===id?{...s,[field]:{...s[field],[m]:val}}:s));
  const setAll = (id,val)   => setStaff(prev=>prev.map(s=>s.id===id?{...s,[field]:mkMap(months,toN(val))}:s));
  const updName= (id,name)  => setStaff(prev=>prev.map(s=>s.id===id?{...s,name}:s));
  const applyPaste = (preview) => {
    setStaff(prev => {
      const updated = [...prev];
      preview.rows.forEach((r, i) => {
        const idx = findIndexByName(updated, r.name, !r.name ? i : -1);
        const vals = Object.fromEntries(months.map((m,j)=>[m, r.values[j] ?? 0]));
        if (idx >= 0) {
          updated[idx] = {...updated[idx], [field]: vals};
        } else if (r.name) {
          updated.push({
            id: makeId("staff"),
            name: r.name,
            salary: mkMap(months, 0),
            bonus: mkMap(months, 0),
            transport: mkMap(months, 0),
            [field]: vals
          });
        }
      });
      return updated;
    });
  };
  return (
    <div>
      <PastePanel mode="person_row" onApply={applyPaste} months={months}/>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
          <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}>
            <th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"80px"}}>氏名</th>
            <th style={{textAlign:"center",padding:"5px 5px",color:"#888",width:"44px"}}>一括</th>
            {months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 2px",color:"#888",minWidth:"66px"}}>{m}</th>)}
            <th style={{textAlign:"right",padding:"5px 8px",color:"#888",minWidth:"72px",position:"sticky",right:"26px",background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>年計</th>
            <th style={{width:"26px",position:"sticky",right:0,background:"#fff"}}></th>
          </tr></thead>
          <tbody>
            {staff.map((s,si)=>(
              <tr key={s.id} style={{borderBottom:"1px solid #F0F4F0",background:si%2===0?"#FAFCFA":"#fff"}}>
                <td style={{padding:"4px 8px"}}>
                  {editName===`${s.id}-${field}`
                    ? <input autoFocus value={s.name} onChange={e=>updName(s.id,e.target.value)} onBlur={()=>setEditName(null)} style={{width:"72px",border:"1px solid #2D7A2D",borderRadius:"3px",padding:"2px 4px",fontSize:"12px"}}/>
                    : <span onClick={()=>setEditName(`${s.id}-${field}`)} style={{cursor:"pointer",color,textDecoration:"underline dotted",fontFamily:"'Noto Sans JP',sans-serif"}}>{s.name}</span>
                  }
                </td>
                <td style={{padding:"4px 5px",textAlign:"center"}}><BulkBtn onSet={val=>setAll(s.id,val)}/></td>
                {months.map(m=><td key={m} style={{padding:"3px 1px",textAlign:"right"}}><NI value={s[field][m]} onChange={val=>upd(s.id,m,val)} w="64px"/></td>)}
                <td style={{padding:"4px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#1A3A1A",position:"sticky",right:"26px",background:si%2===0?"#FAFCFA":"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((sum,m)=>sum+toN(s[field][m]),0).toLocaleString()}</td>
                <td style={{position:"sticky",right:0,background:si%2===0?"#FAFCFA":"#fff"}}><button onClick={()=>remove(s.id)} style={{background:"none",border:"none",color:"#C0392B",cursor:"pointer",fontSize:"14px"}}>×</button></td>
              </tr>
            ))}
            <tr style={{borderTop:"2px solid #E8EDE8",background:"#F5FAF5"}}>
              <td colSpan={2} style={{padding:"5px 8px",fontWeight:"700",color,fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>合計</td>
              {months.map(m=><td key={m} style={{padding:"5px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#1A3A1A",fontSize:"12px"}}>{staff.reduce((s,p)=>s+toN(p[field][m]),0).toLocaleString()}</td>)}
              <td style={{padding:"5px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color,fontSize:"13px",position:"sticky",right:"26px",background:"#F5FAF5",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{staff.reduce((s,p)=>s+months.reduce((ss,m)=>ss+toN(p[field][m]),0),0).toLocaleString()}</td>
              <td style={{position:"sticky",right:0,background:"#F5FAF5"}}></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── SALARY EDITOR ────────────────────────────────────────────

function SalaryEditor({staff, setStaff, months}) {
  const add = () => setStaff(prev=>[...prev,{id:makeId("staff"),name:"新規",salary:mkMap(months,0),bonus:mkMap(months,0),transport:mkMap(months,0)}]);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
          <ST>給与設定</ST>
          <button onClick={add} style={{padding:"6px 14px",background:"#2D7A2D",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>＋ 人員追加</button>
        </div>
        <p style={{fontSize:"11px",color:"#888",marginBottom:"16px",fontFamily:"'Noto Sans JP',sans-serif"}}>合計が「経費設定」の給料に自動反映されます。</p>
        <SalarySectionTable field="salary" label="給与" color="#2D7A2D" staff={staff} setStaff={setStaff} months={months}/>
      </Card>
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
          <ST>賞与設定</ST>
          <button onClick={add} style={{padding:"6px 14px",background:"#AA7722",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>＋ 人員追加</button>
        </div>
        <p style={{fontSize:"11px",color:"#888",marginBottom:"16px",fontFamily:"'Noto Sans JP',sans-serif"}}>合計が「経費設定」の賞与に自動反映されます。</p>
        <SalarySectionTable field="bonus" label="賞与" color="#AA7722" staff={staff} setStaff={setStaff} months={months}/>
      </Card>
    </div>
  );
}
// ─── TRANSPORT EDITOR ─────────────────────────────────────────

function TransportEditor({staff, setStaff, months}) {
  const [editName, setEditName] = useState(null);
  const upd    = (id,m,val) => setStaff(prev=>prev.map(s=>s.id===id?{...s,transport:{...s.transport,[m]:val}}:s));
  const setAll = (id,val)   => setStaff(prev=>prev.map(s=>s.id===id?{...s,transport:mkMap(months,toN(val))}:s));
  const updName= (id,name)  => setStaff(prev=>prev.map(s=>s.id===id?{...s,name}:s));
  const add    = () => setStaff(prev=>[...prev,{id:makeId("staff"),name:"新規",salary:mkMap(months,0),bonus:mkMap(months,0),transport:mkMap(months,0)}]);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
          <ST>交通費設定</ST>
          <button onClick={add} style={{padding:"6px 14px",background:"#2D7A2D",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>＋ 人員追加</button>
        </div>
        <p style={{fontSize:"11px",color:"#888",marginBottom:"16px",fontFamily:"'Noto Sans JP',sans-serif"}}>人員ごとの月次交通費を設定します。合計が「経費設定」に自動反映されます（経費設定側での手動入力も可）。</p>
        <PastePanel mode="person_row" onApply={(preview)=>{
          setStaff(prev => {
            const updated = [...prev];
            preview.rows.forEach((r, i) => {
              const idx = findIndexByName(updated, r.name, !r.name ? i : -1);
              const vals = Object.fromEntries(months.map((m,j)=>[m, r.values[j] ?? 0]));
              if (idx >= 0) {
                updated[idx] = {...updated[idx], transport: vals, name: r.name || updated[idx].name};
              } else if (r.name) {
                updated.push({
                  id: makeId("staff"),
                  name: r.name,
                  salary: mkMap(months, 0),
                  bonus: mkMap(months, 0),
                  transport: vals
                });
              }
            });
            return updated;
          });
        }} months={months}/>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
            <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}>
              <th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"80px"}}>氏名</th>
              <th style={{textAlign:"center",padding:"5px 5px",color:"#888",width:"44px"}}>一括</th>
              {months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 2px",color:"#888",minWidth:"66px"}}>{m}</th>)}
              <th style={{textAlign:"right",padding:"5px 8px",color:"#888",minWidth:"72px",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>年計</th>
            </tr></thead>
            <tbody>
              {staff.map((s,si)=>(
                <tr key={s.id} style={{borderBottom:"1px solid #F0F4F0",background:si%2===0?"#FAFCFA":"#fff"}}>
                  <td style={{padding:"4px 8px"}}>
                    {editName===s.id
                      ? <input autoFocus value={s.name} onChange={e=>updName(s.id,e.target.value)} onBlur={()=>setEditName(null)} style={{width:"72px",border:"1px solid #2D7A2D",borderRadius:"3px",padding:"2px 4px",fontSize:"12px"}}/>
                      : <span onClick={()=>setEditName(s.id)} style={{cursor:"pointer",color:"#2D7A2D",textDecoration:"underline dotted",fontFamily:"'Noto Sans JP',sans-serif"}}>{s.name}</span>
                    }
                  </td>
                  <td style={{padding:"4px 5px",textAlign:"center"}}><BulkBtn onSet={val=>setAll(s.id,val)}/></td>
                  {months.map(m=><td key={m} style={{padding:"3px 1px",textAlign:"right"}}><NI value={s.transport[m]} onChange={val=>upd(s.id,m,val)} w="64px"/></td>)}
                  <td style={{padding:"4px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#1A3A1A",position:"sticky",right:0,background:si%2===0?"#FAFCFA":"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((sum,m)=>sum+toN(s.transport[m]),0).toLocaleString()}</td>
                </tr>
              ))}
              <tr style={{borderTop:"2px solid #E8EDE8",background:"#F5FAF5"}}>
                <td colSpan={2} style={{padding:"5px 8px",fontWeight:"700",color:"#2D7A2D",fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>月次合計</td>
                {months.map(m=><td key={m} style={{padding:"5px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#1A3A1A",fontSize:"12px"}}>{staff.reduce((s,p)=>s+toN(p.transport[m]),0).toLocaleString()}</td>)}
                <td style={{padding:"5px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:"#2D7A2D",fontSize:"13px",position:"sticky",right:0,background:"#F5FAF5",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{staff.reduce((s,p)=>s+months.reduce((ss,m)=>ss+toN(p.transport[m]),0),0).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── EXPENSE EDITOR ───────────────────────────────────────────

function ExpenseEditor({expenses, setExpenses, expenseItems, setExpenseItems, expOverride, setExpOverride, staff, months, expenseMemos, setExpenseMemos}) {
  const [pasteConfirm, setPasteConfirm] = useState(null); // {preview, autoIds, newItems}
  const [editLabelId,  setEditLabelId]  = useState(null);
  const [showMemos, setShowMemos] = useState(false);

  const autoVals = {
    salary:    Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.salary[m]),0)])),
    bonus:     Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.bonus[m]),0)])),
    transport: Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.transport[m]),0)])),
  };
  const getV = (id,m) => {
    const item = expenseItems.find(e=>e.id===id);
    if (item?.auto && !expOverride[id]) return toN(autoVals[item.autoKey]?.[m]);
    return toN(expenses[id]?.[m]);
  };
  const upd    = (id,m,val) => setExpenses(prev=>({...prev,[id]:{...prev[id],[m]:val}}));
  const setAll = (id,val)   => setExpenses(prev=>({...prev,[id]:mkMap(months,toN(val))}));
  const getMemo = (id, m) => expenseMemos?.[id]?.[m] ?? "";
  const hasMemo = (id, m) => getMemo(id, m).trim() !== "";
  const updMemo = (id, m, val) => setExpenseMemos(prev => ({...prev, [id]: {...(prev?.[id] || {}), [m]: val}}));

  // 科目名変更
  const updLabel = (id, label) => setExpenseItems(prev=>prev.map(e=>e.id===id?{...e,label}:e));

  // 科目追加
  const addItem = () => {
    const id = makeId("custom");
    setExpenseItems(prev=>[...prev, {id, label:"新規科目", auto:false}]);
    setExpenses(prev=>({...prev, [id]: mkMap(months, 0)}));
    setEditLabelId(id);
  };

  // 科目削除（自動科目は削除不可）
  const removeItem = (id) => {
    const item = expenseItems.find(e=>e.id===id);
    if (item?.auto) { alert(`「${item.label}」は自動連携科目のため削除できません。`); return; }
    if (!window.confirm(`「${item?.label}」を削除しますか？`)) return;
    setExpenseItems(prev=>prev.filter(e=>e.id!==id));
    setExpenses(prev=>{ const n={...prev}; delete n[id]; return n; });
  };

  // ペースト適用（科目名マッチング）
  const applyExpPaste = (preview) => {
    const newItems = [...expenseItems];
    const newExp   = {...expenses};
    const autoMatchIds = [];

    preview.rows.forEach((r, i) => {
      const pastedLabel = (r.label ?? r.name ?? "").trim();
      let matched = null;
      if (pastedLabel) {
        matched = newItems.find(e => normalizeKey(e.label) === normalizeKey(pastedLabel)) || null;
      }
      if (!matched && !pastedLabel && i < newItems.length) matched = newItems[i];

      if (matched) {
        if (matched.auto && !expOverride[matched.id]) autoMatchIds.push(matched.id);
        newExp[matched.id] = Object.fromEntries(months.map((m,j)=>[m, String(r.values[j] ?? 0)]));
      } else if (pastedLabel) {
        const id = makeId("custom");
        newItems.push({id, label:pastedLabel, auto:false});
        newExp[id] = Object.fromEntries(months.map((m,j)=>[m, String(r.values[j] ?? 0)]));
      }
    });

    const uniqueAutoIds = [...new Set(autoMatchIds)];
    if (uniqueAutoIds.length > 0) {
      setPasteConfirm({autoIds: uniqueAutoIds, newItems, newExp});
    } else {
      setExpenseItems(newItems);
      setExpenses(newExp);
    }
  };

  const doApplyExpPaste = (newItems, newExp, forceManualIds) => {
    const newOverride = {...expOverride};
    forceManualIds.forEach(id => { newOverride[id] = true; });
    setExpOverride(newOverride);
    setExpenseItems(newItems);
    setExpenses(newExp);
    setPasteConfirm(null);
  };
  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      {pasteConfirm && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:"10px",padding:"24px",maxWidth:"420px",width:"90%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"15px",fontWeight:"700",color:"#1A3A1A",marginBottom:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>⚠️ 自動モードの科目を上書き</div>
            <p style={{fontSize:"12px",color:"#555",lineHeight:"1.7",fontFamily:"'Noto Sans JP',sans-serif",marginBottom:"16px"}}>
              以下の科目は現在「自動」モードです。貼り付けると「手動」に切り替わります。
            </p>
            <div style={{background:"#FFF8E1",border:"1px solid #FFD54F",borderRadius:"6px",padding:"10px 14px",marginBottom:"16px"}}>
              {pasteConfirm.autoIds.map(id => (
                <div key={id} style={{fontSize:"12px",color:"#AA5500",fontFamily:"'Noto Sans JP',sans-serif"}}>
                  ・{expenseItems.find(e=>e.id===id)?.label}
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={()=>setPasteConfirm(null)} style={{padding:"8px 16px",background:"#F0F4F0",border:"1px solid #D0D8D0",borderRadius:"6px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",color:"#555"}}>
                キャンセル
              </button>
              <button onClick={()=>doApplyExpPaste(pasteConfirm.newItems, pasteConfirm.newExp, pasteConfirm.autoIds)} style={{padding:"8px 16px",background:"#AA5500",color:"#fff",border:"none",borderRadius:"6px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",fontWeight:"700"}}>
                手動に切替えて貼り付け
              </button>
            </div>
          </div>
        </div>
      )}
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}>
          <ST>経費設定</ST>
          <button onClick={()=>setShowMemos(v=>!v)} style={{padding:"5px 10px",fontSize:"11px",border:"1px solid #D0D8D0",borderRadius:"6px",background:showMemos?"#F0F4F0":"#fff",cursor:"pointer",fontFamily:"'Noto Sans JP',sans-serif"}}>📝 メモ表示</button>
        </div>
        <p style={{fontSize:"11px",color:"#888",marginBottom:"16px",fontFamily:"'Noto Sans JP',sans-serif"}}>給料・交通費・賞与は各タブの設定が自動反映（緑）されます。「手動」ボタンで直接入力に切り替えられます。科目名はクリックで編集できます。</p>
        <PastePanel mode="expense_row" onApply={applyExpPaste} months={months}/>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
            <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}>
              <th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"110px"}}>科目</th>
              <th style={{textAlign:"center",padding:"5px 5px",color:"#888",width:"48px"}}>モード</th>
              <th style={{textAlign:"center",padding:"5px 4px",color:"#888",width:"44px"}}>一括</th>
              {months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 2px",color:"#888",minWidth:"66px"}}>{m}</th>)}
              <th style={{textAlign:"right",padding:"5px 8px",color:"#888",minWidth:"72px",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>合計</th>
              <th style={{textAlign:"center",padding:"5px 4px",color:"#888",width:"28px"}}></th>
            </tr></thead>
            <tbody>
              {expenseItems.map((item,ii)=>{
                const isAuto = item.auto && !expOverride[item.id];
                return (
                  <tr key={item.id} style={{borderBottom:"1px solid #F0F4F0",background:ii%2===0?"#FAFCFA":"#fff"}}>
                    <td style={{padding:"4px 8px"}}>
                      {editLabelId===item.id
                        ? <input autoFocus value={item.label}
                            onChange={e=>updLabel(item.id, e.target.value)}
                            onBlur={()=>setEditLabelId(null)}
                            onKeyDown={e=>e.key==="Enter"&&setEditLabelId(null)}
                            style={{width:"90px",border:"1px solid #C0392B",borderRadius:"3px",padding:"2px 4px",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}/>
                        : <span onClick={()=>setEditLabelId(item.id)}
                            style={{cursor:"pointer",color:"#444",fontFamily:"'Noto Sans JP',sans-serif",borderBottom:"1px dotted #ccc",paddingBottom:"1px"}}
                            title="クリックで科目名を編集">
                            {item.label}
                          </span>
                      }
                    </td>
                    <td style={{padding:"4px 4px",textAlign:"center"}}>
                      {item.auto
                        ? <button onClick={()=>setExpOverride(prev=>({...prev,[item.id]:!prev[item.id]}))}
                            style={{fontSize:"10px",padding:"2px 7px",background:isAuto?"#E8F4E8":"#FFF3E0",color:isAuto?"#2D7A2D":"#AA5500",border:`1px solid ${isAuto?"#C8E6C8":"#FFCC80"}`,borderRadius:"3px",cursor:"pointer",whiteSpace:"nowrap"}}>
                            {isAuto?"自動":"手動"}
                          </button>
                        : <span style={{fontSize:"10px",color:"#bbb"}}>—</span>
                      }
                    </td>
                    <td style={{padding:"4px 4px",textAlign:"center"}}>
                      {!isAuto ? <BulkBtn onSet={val=>setAll(item.id,val)}/> : <span style={{fontSize:"10px",color:"#bbb"}}>—</span>}
                    </td>
                    {months.map(m=>(
                      <td key={m} style={{padding:"3px 1px",textAlign:"right",position:"relative",verticalAlign:"top"}}>
                        {hasMemo(item.id, m) && !showMemos && <span style={{position:"absolute",top:"0px",right:"4px",fontSize:"9px",color:"#666"}}>●</span>}
                        {isAuto
                          ? <span style={{display:"inline-block",width:"64px",padding:"4px 3px",textAlign:"right",fontSize:"12px",fontFamily:"monospace",color:"#2D7A2D",background:"#F0FAF0",borderRadius:"4px"}}>{toN(autoVals[item.autoKey]?.[m]).toLocaleString()}</span>
                          : <NI value={getV(item.id,m)} onChange={val=>upd(item.id,m,val)} w="64px"/>
                        }
                        {showMemos && (
                          <input type="text" value={getMemo(item.id, m)} onChange={e=>updMemo(item.id, m, e.target.value)} style={{marginTop:"3px",width:"64px",boxSizing:"border-box",padding:"2px 4px",fontSize:"10px",border:"1px solid #E0E0E0",borderRadius:"4px",background:"#F5F5F5"}} />
                        )}
                      </td>
                    ))}
                    <td style={{padding:"4px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:isAuto?"#2D7A2D":"#1A3A1A",position:"sticky",right:0,background:ii%2===0?"#FAFCFA":"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((s,m)=>s+getV(item.id,m),0).toLocaleString()}</td>
                    <td style={{padding:"4px 4px",textAlign:"center"}}>
                      {item.auto
                        ? <span title="自動連携科目は削除できません" style={{fontSize:"11px",color:"#e0e0e0",padding:"2px 4px",lineHeight:1,cursor:"not-allowed"}}>✕</span>
                        : <button onClick={()=>removeItem(item.id)} title="この科目を削除"
                            style={{fontSize:"11px",color:"#ccc",background:"none",border:"none",cursor:"pointer",padding:"2px 4px",borderRadius:"3px",lineHeight:1}}
                            onMouseEnter={e=>e.target.style.color="#C0392B"}
                            onMouseLeave={e=>e.target.style.color="#ccc"}>✕</button>
                      }
                    </td>
                  </tr>
                );
              })}
              <tr style={{background:"#F8FBF8"}}>
                <td colSpan={3+months.length+2} style={{padding:"6px 8px"}}>
                  <button onClick={addItem} style={{fontSize:"11px",color:"#2D7A2D",background:"none",border:"1px dashed #C8E6C8",borderRadius:"4px",cursor:"pointer",padding:"3px 12px",fontFamily:"'Noto Sans JP',sans-serif"}}>
                    ＋ 科目を追加
                  </button>
                </td>
              </tr>
              <tr style={{borderTop:"2px solid #E8EDE8",background:"#FFF5F5"}}>
                <td colSpan={3} style={{padding:"6px 8px",fontWeight:"700",color:"#C0392B",fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>月次合計</td>
                {months.map(m=><td key={m} style={{padding:"6px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#C0392B",fontSize:"12px"}}>{expenseItems.reduce((s,item)=>s+getV(item.id,m),0).toLocaleString()}</td>)}
                <td style={{padding:"6px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:"#C0392B",fontSize:"13px",position:"sticky",right:0,background:"#FFF5F5",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{expenseItems.reduce((s,item)=>s+months.reduce((ss,m)=>ss+getV(item.id,m),0),0).toLocaleString()}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── ACTUALS EDITOR ───────────────────────────────────────────

function ActualsEditor({actuals, setActuals, revenue, expenses, expenseItems, expOverride, staff, months, taxNote, onChangeTaxNote}) {
  const [detailMode, setDetailMode] = useState(false);
  const accent = "#553399";
  const simple = useMemo(() => normalizeSimpleActuals(actuals.simple, months), [actuals.simple, months]);

  const syncSimple = (nextSimple) => {
    const normalized = normalizeSimpleActuals(nextSimple, months);
    setActuals(prev => ({ ...prev, simple: normalized }));
  };

  const taxNoteBlock = (
    <div style={{
      background:"#FFFBF0",
      border:"1px solid #E8D8A0",
      borderLeft:"4px solid #AA7722",
      borderRadius:"8px",
      padding:"14px 16px",
      marginBottom:"16px",
    }}>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"8px"}}>
        <span style={{fontSize:"11px", fontWeight:"700", color:"#AA7722", fontFamily:"'Noto Sans JP',sans-serif", letterSpacing:"0.03em"}}>税理士短評</span>
        <span style={{fontSize:"10px", color:"#bbb", fontFamily:"'Noto Sans JP',sans-serif"}}>ダッシュボードに表示されます</span>
      </div>
      <textarea
        value={taxNote}
        onChange={e => onChangeTaxNote(e.target.value)}
        placeholder="今月のポイントや気になる点をここに記入（例：カード利用が予算比+15%。Q3に向けて要確認）"
        rows={3}
        style={{width:"100%", boxSizing:"border-box", border:"1px solid #E8D8A0", borderRadius:"6px", padding:"8px 10px", fontSize:"12px", fontFamily:"'Noto Sans JP',sans-serif", lineHeight:"1.7", color:"#3A2000", background:"#FFFEF8", resize:"vertical", outline:"none"}}
      />
    </div>
  );

  const getSimpleItems = (type) => simple?.[type] || [];
  const getSimpleMonthlyValue = (type, itemId, m) => getSimpleItems(type).find(item=>item.id===itemId)?.monthly?.[m] ?? "";
  const updateSimpleItemValue = (type, itemId, m, val) => {
    syncSimple({
      ...simple,
      [type]: getSimpleItems(type).map(item => item.id===itemId ? {...item, monthly:{...item.monthly, [m]:val}} : item),
    });
  };
  const updateSimpleItemName = (type, itemId, name) => {
    syncSimple({
      ...simple,
      [type]: getSimpleItems(type).map(item => item.id===itemId ? {...item, name} : item),
    });
  };
  const addSimpleItem = (type) => {
    const prefix = type === "revItems" ? "simpleRev" : "simpleExp";
    const defaultName = type === "revItems" ? "新規売上" : "新規費用";
    syncSimple({ ...simple, [type]: [...getSimpleItems(type), {id:makeId(prefix), name:defaultName, monthly:mkMap(months,"")}] });
  };
  const removeSimpleItem = (type, itemId) => {
    if (getSimpleItems(type).length <= 1) return;
    syncSimple({ ...simple, [type]: getSimpleItems(type).filter(item => item.id !== itemId) });
  };
  const getSimpleTotal = (type, m) => getSimpleItems(type).reduce((s,item)=>s+toN(item.monthly?.[m]),0);
  const hasSimpleAny = (type, m) => getSimpleItems(type).some(item => (item.monthly?.[m] ?? "") !== "");
  const applySimpleRowPaste = (type, itemId, preview) => {
    const row = preview.rows?.[0];
    if (!row) return;
    syncSimple({
      ...simple,
      [type]: getSimpleItems(type).map(item => item.id===itemId ? {...item, monthly:Object.fromEntries(months.map((m,j)=>[m, String(row.values?.[j] ?? 0)]))} : item),
    });
  };

  const updRevItem = (revId, m, val) => setActuals(prev=>({
    ...prev,
    revItems: {...(prev.revItems||{}), [revId]: {...((prev.revItems||{})[revId]||{}), [m]:val}}
  }));
  const updExpItem = (id, m, val) => setActuals(prev=>({
    ...prev,
    items:{...prev.items, [id]:{...(prev.items?.[id]||{}), [m]:val}}
  }));
  const applyRevPaste = (preview) => {
    const newRevItems = {...(actuals.revItems||{})};
    preview.rows.forEach((r, i) => {
      const key = normalizeKey(r.name || r.label);
      let rev = null;
      if (key) rev = revenue.find(item => normalizeKey(item.name) === key) || null;
      if (!rev && !r.name && !r.label && i < revenue.length) rev = revenue[i];
      if (!rev) return;
      newRevItems[rev.id] = Object.fromEntries(months.map((m,j)=>[m, String(r.values[j] ?? 0)]));
    });
    setActuals(prev=>({...prev, revItems:newRevItems}));
  };
  const applyExpPaste = (preview) => {
    const newItems = {...(actuals.items||{})};
    preview.rows.forEach((r, i) => {
      const key = normalizeKey(r.label || r.name);
      let item = null;
      if (key) item = expenseItems.find(exp => normalizeKey(exp.label) === key) || null;
      if (!item && !r.label && !r.name && i < expenseItems.length) item = expenseItems[i];
      if (!item) return;
      newItems[item.id] = Object.fromEntries(months.map((m,j)=>[m, String(r.values[j] ?? 0)]));
    });
    setActuals(prev=>({...prev, items:newItems}));
  };

  const getActRevV = (revId, m) => actuals.revItems?.[revId]?.[m] ?? "";
  const getActExpV = (id, m)   => actuals.items?.[id]?.[m] ?? "";



  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      {taxNoteBlock}
      <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:"8px"}}>
        <span style={{fontSize:"11px",color:"#888",fontFamily:"'Noto Sans JP',sans-serif"}}>入力モード：</span>
        <div style={{display:"flex",border:"1px solid #D0D8D0",borderRadius:"6px",overflow:"hidden"}}>
          <button onClick={()=>setDetailMode(false)} style={{padding:"6px 16px", fontSize:"12px", fontFamily:"'Noto Sans JP',sans-serif", fontWeight:!detailMode?"700":"400", background:!detailMode?accent:"#fff", color:!detailMode?"#fff":"#888", border:"none", cursor:"pointer", transition:"all 0.15s"}}>簡易</button>
          <button onClick={()=>setDetailMode(true)} style={{padding:"6px 16px", fontSize:"12px", fontFamily:"'Noto Sans JP',sans-serif", fontWeight:detailMode?"700":"400", background:detailMode?accent:"#fff", color:detailMode?"#fff":"#888", border:"none", borderLeft:"1px solid #D0D8D0", cursor:"pointer", transition:"all 0.15s"}}>詳細</button>
        </div>
      </div>

      {!detailMode && (
        <Card>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
            <ST>実績入力（簡易）</ST>
            <span style={{fontSize:"11px",color:"#888",fontFamily:"'Noto Sans JP',sans-serif"}}>自由項目を追加して合計を自動計算</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr",gap:"18px"}}>
            {[{key:"revItems", title:"売上自由項目", color:"#2D7A2D", bg:"#E8F4E8", pasteMode:"revenue_row"}, {key:"expItems", title:"費用自由項目", color:"#C0392B", bg:"#FFF5F5", pasteMode:"revenue_row"}].map(section => (
              <div key={section.key}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}>
                  <div style={{fontSize:"12px",fontWeight:"700",color:section.color,fontFamily:"'Noto Sans JP',sans-serif"}}>{section.title}</div>
                  <button onClick={()=>addSimpleItem(section.key)} style={{fontSize:"11px",padding:"4px 10px",background:"#fff",border:`1px dashed ${section.color}`,borderRadius:"6px",cursor:"pointer",color:section.color,fontFamily:"'Noto Sans JP',sans-serif"}}>＋ 項目追加</button>
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
                  <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}><th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"120px"}}>項目</th>{months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 2px",color:"#888",minWidth:"66px"}}>{m}</th>)}<th style={{textAlign:"right",padding:"5px 8px",color:"#888",minWidth:"72px",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>合計</th><th style={{width:"96px"}}></th></tr></thead>
                  <tbody>
                    {getSimpleItems(section.key).map((item, idx)=>(
                      <React.Fragment key={item.id}>
                        <tr style={{borderBottom:"1px solid #F0F4F0",background:idx%2===0?"#FAFCFA":"#fff"}}>
                          <td style={{padding:"4px 8px"}}><input value={item.name} onChange={e=>updateSimpleItemName(section.key, item.id, e.target.value)} style={{width:"100px",border:"1px solid #D0D8D0",borderRadius:"4px",padding:"3px 6px",fontSize:"12px"}}/></td>
                          {months.map(m=><td key={m} style={{padding:"3px 1px",textAlign:"right"}}><NI value={getSimpleMonthlyValue(section.key, item.id, m)} onChange={val=>updateSimpleItemValue(section.key, item.id, m, val)} w="64px" style={{border:`1px solid ${section.color}`}}/></td>)}
                          <td style={{padding:"4px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:section.color,position:"sticky",right:0,background:idx%2===0?"#FAFCFA":"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((s,m)=>s+toN(getSimpleMonthlyValue(section.key, item.id, m)),0).toLocaleString()}</td>
                          <td style={{padding:"4px 8px",whiteSpace:"nowrap"}}>
                            <details>
                              <summary style={{cursor:"pointer",fontSize:"11px",color:section.color}}>貼り付け</summary>
                              <div style={{marginTop:"6px"}}><PastePanel mode={section.pasteMode} onApply={(preview)=>applySimpleRowPaste(section.key, item.id, preview)} months={months}/></div>
                            </details>
                            {getSimpleItems(section.key).length > 1 && <button onClick={()=>removeSimpleItem(section.key, item.id)} style={{marginTop:"4px",fontSize:"11px",border:"none",background:"none",color:"#999",cursor:"pointer"}}>削除</button>}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                    <tr style={{borderTop:"2px solid #E8EDE8",background:section.bg}}>
                      <td style={{padding:"6px 8px",fontWeight:"800",color:section.color,fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>合計</td>
                      {months.map(m=><td key={m} style={{padding:"6px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:section.color,fontSize:"12px"}}>{getSimpleTotal(section.key, m).toLocaleString()}</td>)}
                      <td style={{padding:"6px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:section.color,fontSize:"13px",position:"sticky",right:0,background:section.bg,boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((s,m)=>s+getSimpleTotal(section.key, m),0).toLocaleString()}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          <div style={{marginTop:"18px",overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
              <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}><th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"90px"}}>項目</th>{months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 2px",color:"#888",minWidth:"66px"}}>{m}</th>)}<th style={{textAlign:"right",padding:"5px 8px",color:"#888",minWidth:"72px",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>合計</th></tr></thead>
              <tbody>
                <tr style={{borderBottom:"1px solid #F0F4F0",background:"#FAFCFA"}}><td style={{padding:"4px 8px",fontFamily:"'Noto Sans JP',sans-serif",color:"#2D7A2D",fontWeight:"700"}}>総売上</td>{months.map(m=><td key={m} style={{padding:"4px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#2D7A2D"}}>{getSimpleTotal("revItems", m).toLocaleString()}</td>)}<td style={{padding:"4px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#2D7A2D",position:"sticky",right:0,background:"#FAFCFA",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((s,m)=>s+getSimpleTotal("revItems", m),0).toLocaleString()}</td></tr>
                <tr style={{borderBottom:"1px solid #F0F4F0",background:"#fff"}}><td style={{padding:"4px 8px",fontFamily:"'Noto Sans JP',sans-serif",color:"#C0392B",fontWeight:"700"}}>総費用</td>{months.map(m=><td key={m} style={{padding:"4px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#C0392B"}}>{getSimpleTotal("expItems", m).toLocaleString()}</td>)}<td style={{padding:"4px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#C0392B",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((s,m)=>s+getSimpleTotal("expItems", m),0).toLocaleString()}</td></tr>
                <tr style={{borderTop:"2px solid #E8EDE8",background:"#F5F0FF"}}><td style={{padding:"6px 8px",fontWeight:"700",color:accent,fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>利益実績</td>{months.map(m=>{const rev=getSimpleTotal("revItems", m);const exp=getSimpleTotal("expItems", m);const hasData=hasSimpleAny("revItems", m)||hasSimpleAny("expItems", m);const profit=rev-exp;return <td key={m} style={{padding:"6px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",fontSize:"12px",color:!hasData?"#ccc":profit>=0?"#2D7A2D":"#C0392B"}}>{hasData?profit.toLocaleString():"—"}</td>;})}<td style={{padding:"6px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:accent,fontSize:"13px",position:"sticky",right:0,background:"#F5F0FF",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{(months.reduce((s,m)=>s+getSimpleTotal("revItems", m),0)-months.reduce((s,m)=>s+getSimpleTotal("expItems", m),0)).toLocaleString()}</td></tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {detailMode && (<>
        <Card>
          <ST>売上実績入力（詳細）</ST>
          <p style={{fontSize:"11px",color:"#888",marginBottom:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>収入設定の項目ごとに実績を入力します。</p>
          <PastePanel mode="revenue_row" onApply={applyRevPaste} months={months}/>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
              <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}><th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"90px"}}>案件名</th>{months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 2px",color:"#888",minWidth:"66px"}}>{m}</th>)}<th style={{textAlign:"right",padding:"5px 8px",color:"#888",minWidth:"72px",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>合計</th></tr></thead>
              <tbody>
                {revenue.map((r,ri)=>(<tr key={r.id} style={{borderBottom:"1px solid #F0F4F0",background:ri%2===0?"#FAFCFA":"#fff"}}><td style={{padding:"4px 8px",fontFamily:"'Noto Sans JP',sans-serif",color:"#2D7A2D",fontWeight:"600"}}>{r.name}</td>{months.map(m=><td key={m} style={{padding:"3px 1px",textAlign:"right"}}><NI value={getActRevV(r.id,m)} onChange={val=>updRevItem(r.id,m,val)} style={{background:"#fff",border:"1px solid #2D7A2D"}} w="64px"/></td>)}<td style={{padding:"4px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#2D7A2D",position:"sticky",right:0,background:ri%2===0?"#FAFCFA":"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((s,m)=>s+toN(getActRevV(r.id,m)),0).toLocaleString()}</td></tr>))}
                <tr style={{borderTop:"2px solid #E8EDE8",background:"#E8F4E8"}}><td style={{padding:"6px 8px",fontWeight:"800",color:"#1A3A1A",fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>合計</td>{months.map(m=><td key={m} style={{padding:"6px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#1A3A1A",fontSize:"12px"}}>{revenue.reduce((s,r)=>s+toN(getActRevV(r.id,m)),0).toLocaleString()}</td>)}<td style={{padding:"6px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:"#2D7A2D",fontSize:"13px",position:"sticky",right:0,background:"#E8F4E8",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{revenue.reduce((s,r)=>s+months.reduce((ss,m)=>ss+toN(getActRevV(r.id,m)),0),0).toLocaleString()}</td></tr>
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <ST>経費実績入力（詳細）</ST>
          <p style={{fontSize:"11px",color:"#888",marginBottom:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>経費設定と同じ科目構成で実績を入力します。</p>
          <PastePanel mode="expense_row" onApply={applyExpPaste} months={months}/>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
              <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}><th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"90px"}}>科目</th>{months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 2px",color:"#888",minWidth:"66px"}}>{m}</th>)}<th style={{textAlign:"right",padding:"5px 8px",color:"#888",minWidth:"72px",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>合計</th></tr></thead>
              <tbody>
                {expenseItems.map((item,ii)=>{ const hasAny = months.some(m=>getActExpV(item.id,m)!==""); return (<tr key={item.id} style={{borderBottom:"1px solid #F0F4F0",background:ii%2===0?"#FAFCFA":"#fff"}}><td style={{padding:"4px 8px",fontFamily:"'Noto Sans JP',sans-serif",color:"#444"}}>{item.label}</td>{months.map(m=><td key={m} style={{padding:"3px 1px",textAlign:"right"}}><NI value={getActExpV(item.id,m)} onChange={val=>updExpItem(item.id,m,val)} style={{background:"#fff",border:"1px solid #D8D0C8"}} w="64px"/></td>)}<td style={{padding:"4px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:hasAny?"#C0392B":"#ccc",position:"sticky",right:0,background:ii%2===0?"#FAFCFA":"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((s,m)=>s+toN(getActExpV(item.id,m)),0).toLocaleString()}</td></tr>); })}
                <tr style={{borderTop:"2px solid #E8EDE8",background:"#FFF5F5"}}><td style={{padding:"6px 8px",fontWeight:"700",color:"#C0392B",fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>合計</td>{months.map(m=><td key={m} style={{padding:"6px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#C0392B",fontSize:"12px"}}>{expenseItems.reduce((s,item)=>s+toN(getActExpV(item.id,m)),0).toLocaleString()}</td>)}<td style={{padding:"6px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:"#C0392B",fontSize:"13px",position:"sticky",right:0,background:"#FFF5F5",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{expenseItems.reduce((s,item)=>s+months.reduce((ss,m)=>ss+toN(getActExpV(item.id,m)),0),0).toLocaleString()}</td></tr>
              </tbody>
            </table>
          </div>
        </Card>
      </>)}

    </div>
  );
}


// ─── DASHBOARD ────────────────────────────────────────────────

function Dashboard({revenue, expenses, expenseItems, expOverride, staff, actuals, months, taxNote, diffComments, onChangeDiffComment}) {
  const simple = useMemo(() => normalizeSimpleActuals(actuals.simple, months), [actuals.simple, months]);
  const {
    planRev, planExp, planProf, actRev, actExp, chartData, filled,
    revisedRevTotal, revisedExpTotal, revisedProfTotal, planRevTotal, planExpTotal, planProfTotal,
    revRevDiff, revExpDiff, revProfDiff
  } = useMemo(() => {
    const autoVals = {
      salary:    Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.salary[m]),0)])),
      bonus:     Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.bonus[m]),0)])),
      transport: Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.transport[m]),0)])),
    };
    const itemById = Object.fromEntries(expenseItems.map(item => [item.id, item]));
    const getPlannedExpense = (id,m) => {
      const item = itemById[id];
      if (item?.auto && !expOverride[id]) return toN(autoVals[item.autoKey]?.[m]);
      return toN(expenses[id]?.[m]);
    };
    const planRev  = months.map(m=>revenue.reduce((s,r)=>s+toN(r.monthly[m]),0));
    const planExp  = months.map(m=>expenseItems.reduce((s,item)=>s+getPlannedExpense(item.id,m),0));
    const planProf = months.map((_,i)=>planRev[i]-planExp[i]);
    const actRev = months.map(m=>{
      const hasDetail = revenue.some(r=>(actuals.revItems?.[r.id]?.[m]??"")!=="");
      if (hasDetail) return revenue.reduce((s,r)=>s+toN(actuals.revItems?.[r.id]?.[m]),0);
      const simpleHas = simple.revItems?.some(item => (item.monthly?.[m] ?? "") !== "");
      if (simpleHas) return simple.revItems.reduce((s,item)=>s+toN(item.monthly?.[m]),0);
      return null;
    });
    const actExp = months.map(m=>{
      const hasDetail = actuals.items && expenseItems.some(item=>(actuals.items[item.id]?.[m]??"")!=="");
      if (hasDetail) return expenseItems.reduce((s,item)=>s+toN(actuals.items[item.id]?.[m]),0);
      const simpleHas = simple.expItems?.some(item => (item.monthly?.[m] ?? "") !== "");
      if (simpleHas) return simple.expItems.reduce((s,item)=>s+toN(item.monthly?.[m]),0);
      return null;
    });
    const chartData= months.map((m,i)=>({name:m, 予測利益:planProf[i], 実績利益:actRev[i]!=null&&actExp[i]!=null?actRev[i]-actExp[i]:null}));
    const filled = months.filter((_,i)=>actRev[i]!==null || actExp[i]!==null);
    const revisedRev = months.map((m,i) => actRev[i] != null ? actRev[i] : planRev[i]);
    const revisedExp = months.map((m,i) => actExp[i] != null ? actExp[i] : planExp[i]);
    const revisedProf = months.map((_,i) => revisedRev[i] - revisedExp[i]);
    const revisedRevTotal = revisedRev.reduce((s,v)=>s+v,0);
    const revisedExpTotal = revisedExp.reduce((s,v)=>s+v,0);
    const revisedProfTotal = revisedProf.reduce((s,v)=>s+v,0);
    const planRevTotal = planRev.reduce((s,v)=>s+v,0);
    const planExpTotal = planExp.reduce((s,v)=>s+v,0);
    const planProfTotal = planProf.reduce((s,v)=>s+v,0);
    const revExpDiff = revisedExpTotal - planExpTotal;
    return { planRev, planExp, planProf, actRev, actExp, chartData, filled, revisedRevTotal, revisedExpTotal, revisedProfTotal, planRevTotal, planExpTotal, planProfTotal, revRevDiff: revisedRevTotal - planRevTotal, revExpDiff, revProfDiff: revisedProfTotal - planProfTotal };
  }, [months, revenue, expenses, expenseItems, expOverride, staff, actuals, simple]);

  const [commentMonth, setCommentMonth] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const openCommentForMonth = (m) => { setCommentMonth(m); setCommentDraft(diffComments[m] ?? ""); };
  useEffect(() => { if (!commentMonth) return; const onKeyDown = (e) => { if (e.key === "Escape") setCommentMonth(null); }; window.addEventListener("keydown", onKeyDown); return () => window.removeEventListener("keydown", onKeyDown); }, [commentMonth]);

  const kpis = [
    { label: filled.length > 0 ? "修正見込み売上" : "年間予測売上", value: revisedRevTotal, sub: `${fmtDiff(revRevDiff, true)?.text || "+0"} vs 予測（予測: ${planRevTotal.toLocaleString()}）`, subColor: fmtDiff(revRevDiff, true)?.color || "#888", note: `実績${filled.length}ヶ月 + 予測${12-filled.length}ヶ月` },
    { label: filled.length > 0 ? "修正見込み費用" : "年間予測費用", value: filled.length > 0 ? revisedExpTotal : planExpTotal, sub: filled.length > 0 ? `${fmtDiff(revExpDiff, true)?.text || "+0"} vs 予測（予測: ${planExpTotal.toLocaleString()}）` : "予測ベース", subColor: fmtDiff(revExpDiff, true)?.color || "#888", note: filled.length > 0 ? `実績${filled.length}ヶ月 + 予測${12-filled.length}ヶ月` : "年間予測費用合計" },
    { label: filled.length > 0 ? "修正見込み利益" : "年間予測利益", value: revisedProfTotal, sub: `${fmtDiff(revProfDiff, true)?.text || "+0"} vs 予測（予測: ${planProfTotal.toLocaleString()}）`, subColor: fmtDiff(revProfDiff, true)?.color || "#888", note: `実績${filled.length}ヶ月 + 予測${12-filled.length}ヶ月` },
    { label: "実績入力済み", value: `${filled.length} / 12`, sub: `${(filled.length/12*100).toFixed(0)}%`, subColor: "#888", note: "" },
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      <div style={{background:"#FFFBF0", border:"1px solid #E8D8A0", borderLeft:"4px solid #AA7722", borderRadius:"8px", padding:"14px 16px"}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"8px"}}><span style={{fontSize:"11px", fontWeight:"700", color:"#AA7722", fontFamily:"'Noto Sans JP',sans-serif", letterSpacing:"0.03em"}}>税理士短評</span></div>
        <div style={{minHeight:"60px", padding:"8px 10px", fontSize:"12px", fontFamily:"'Noto Sans JP',sans-serif", lineHeight:"1.7", color: taxNote ? "#3A2000" : "#bbb", background:"#FFFEF8", border:"1px solid #E8D8A0", borderRadius:"6px", whiteSpace:"pre-wrap", wordBreak:"break-all"}}>{taxNote || "税理士からの短評はまだありません。"}</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"12px"}}>
        {kpis.map(k=>(<Card key={k.label} style={{textAlign:"center"}}><div style={{fontSize:"11px",color:"#888",marginBottom:"4px",fontFamily:"'Noto Sans JP',sans-serif"}}>{k.label}</div><div style={{fontSize:"22px",fontWeight:"800",color:"#1A3A1A",fontFamily:"monospace"}}>{typeof k.value === "number" ? k.value.toLocaleString() : k.value}</div><div style={{fontSize:"11px",color:k.subColor,marginTop:"6px",fontWeight:"700"}}>{k.sub}</div><div style={{fontSize:"10px",color:"#aaa",marginTop:"2px"}}>{k.note}</div></Card>))}
      </div>
      <Card>
        <ST>月次利益 予測 vs 実績</ST>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} barGap={2}>
            <XAxis dataKey="name" tick={{fontSize:11,fill:"#888"}} axisLine={false} tickLine={false}/><YAxis tick={{fontSize:11,fill:"#888"}} axisLine={false} tickLine={false} tickFormatter={v=>`${(v/10000).toFixed(0)}万`}/><Tooltip formatter={(v,n)=>[v?.toLocaleString()+" 円",n]}/><Bar dataKey="予測利益" fill="#C8E6C8" radius={[3,3,0,0]}/><Bar dataKey="実績利益" fill="#2D7A2D" radius={[3,3,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card>
        <ST>月次予実サマリ</ST>
        {(()=>{
          // 予実値：実績があれば実績、なければ予測
          const revisedRev  = months.map((m,i) => actRev[i]  != null ? actRev[i]  : planRev[i]);
          const revisedExp  = months.map((m,i) => actExp[i]  != null ? actExp[i]  : planExp[i]);
          // 実績利益
          const actProf  = months.map((m,i) => (actRev[i]!=null && actExp[i]!=null) ? actRev[i]-actExp[i] : null);
          // 予実利益：予実売上 − 予実費用
          const revisedProf = months.map((_,i) => revisedRev[i] - revisedExp[i]);
          // 合計計算
          const totPlanRev   = planRev.reduce((s,v)=>s+v,0);
          const totPlanExp   = planExp.reduce((s,v)=>s+v,0);
          const totPlanProf  = planProf.reduce((s,v)=>s+v,0);
          const totActRev    = actRev.reduce((s,v)=>s+(v??0),0);
          const totActExp    = actExp.reduce((s,v)=>s+(v??0),0);
          const totActProf   = actProf.reduce((s,v)=>s+(v??0),0);
          const totRevRev    = revisedRev.reduce((s,v)=>s+v,0);
          const totRevExp    = revisedExp.reduce((s,v)=>s+v,0);
          const totRevProf   = revisedProf.reduce((s,v)=>s+v,0);

          // グループ色定義（グラフに合わせて：予測=薄緑、実績=緑、予実=青）
          const PRED_C = "#7DB87D";
          const ACT_C  = "#2D7A2D";
          const REV_C  = "#2255AA";

          const rows = [
            // ＜予測＞
            { label:"予測売上", color:PRED_C, bg:"#F5FAF5", vals:planRev,      sum:totPlanRev,  isDiff:false },
            { label:"予測費用", color:PRED_C, bg:"#F0F8F0", vals:planExp,      sum:totPlanExp,  isDiff:false },
            { label:"予測利益", color:PRED_C, bg:"#EBF5EB", vals:planProf,     sum:totPlanProf, isDiff:false },
            // ＜実績＞
            { label:"実績売上", color:ACT_C,  bg:"#FAFCFA", vals:actRev,       sum:totActRev,   isDiff:false, spacer:true },
            { label:"実績費用", color:ACT_C,  bg:"#F5FAF5", vals:actExp,       sum:totActExp,   isDiff:false },
            { label:"実績利益", color:ACT_C,  bg:"#EEF7EE", vals:actProf,      sum:totActProf,  isDiff:false },
            // ＜予実＞：売上・費用は実績があれば実績、なければ予測。利益は予実売上−予実費用
            { label:"予実売上", color:REV_C,  bg:"#F0F5FF", vals:revisedRev,   sum:totRevRev,   isDiff:false, spacer:true },
            { label:"予実費用", color:REV_C,  bg:"#EBF0FF", vals:revisedExp,   sum:totRevExp,   isDiff:false },
            { label:"予実利益", color:REV_C,  bg:"#E5EEFF", vals:revisedProf,  sum:totRevProf,  isDiff:false },
          ];

          return (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px",fontFamily:"monospace"}}>
                <thead>
                  <tr style={{borderBottom:"2px solid #E8EDE8"}}>
                    <th style={{textAlign:"left",padding:"5px 8px",color:"#888",fontWeight:"600",minWidth:"110px",fontFamily:"'Noto Sans JP',sans-serif"}}>項目</th>
                    {months.map(m=><th key={m} onClick={()=>openCommentForMonth(m)} style={{textAlign:"right",padding:"5px 4px",color:"#888",fontWeight:"600",minWidth:"72px",cursor:"pointer"}}>{m}{diffComments[m]?"✏️":""}</th>)}
                    <th style={{textAlign:"right",padding:"5px 8px",color:"#888",fontWeight:"600",minWidth:"80px",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>合計</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row,ri)=>(
                    <React.Fragment key={row.label}>
                      {row.spacer && <tr><td colSpan={months.length+2} style={{height:"10px",padding:"0",background:"transparent"}}></td></tr>}
                      <tr style={{background:row.bg}}>
                        <td style={{padding:"5px 8px",color:row.color,fontWeight:"700",fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>{row.label}</td>
                        {row.vals.map((v,i)=>{
                          if (row.isDiff) {
                            // 差分表示（予実利益）：+/－で色付き
                            const info = fmtDiff(v, true);
                            return <td key={i} style={{padding:"5px 4px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:info?.color||"#ccc"}}>{info?info.text:"—"}</td>;
                          }
                          return <td key={i} style={{padding:"5px 4px",textAlign:"right",color:v!=null?row.color:"#ccc",fontWeight:"600"}}>{v!=null?v.toLocaleString():"—"}</td>;
                        })}
                        {row.isDiff
                          ? (()=>{ const info=fmtDiff(row.sum,true); return <td style={{padding:"5px 8px",textAlign:"right",fontWeight:"800",color:info?.color||"#ccc",position:"sticky",right:0,background:row.bg,boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{info?info.text:"—"}</td>; })()
                          : <td style={{padding:"5px 8px",textAlign:"right",fontWeight:"800",color:row.color,position:"sticky",right:0,background:row.bg,boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{row.sum.toLocaleString()}</td>
                        }
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Card>

      {commentMonth && <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}} onClick={()=>setCommentMonth(null)}><div style={{width:"min(560px, 92vw)",background:"#fff",borderRadius:"12px",padding:"18px",boxShadow:"0 20px 40px rgba(0,0,0,0.18)"}} onClick={(e)=>e.stopPropagation()}><div style={{fontSize:"14px",fontWeight:"800",color:"#1A3A1A",marginBottom:"10px",fontFamily:"'Noto Sans JP',sans-serif"}}>{commentMonth} の差異理由メモ</div><textarea value={commentDraft} onChange={(e)=>setCommentDraft(e.target.value)} placeholder="差異の理由や対応方針を入力" rows={6} style={{width:"100%",boxSizing:"border-box",border:"1px solid #D0D8D0",borderRadius:"8px",padding:"10px 12px",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",lineHeight:"1.7",resize:"vertical"}}/><div style={{display:"flex",justifyContent:"flex-end",gap:"8px",marginTop:"12px"}}><button onClick={()=>setCommentMonth(null)} style={{padding:"7px 14px",border:"1px solid #D0D8D0",borderRadius:"6px",background:"#fff",cursor:"pointer"}}>閉じる</button><button onClick={()=>{onChangeDiffComment(commentMonth, commentDraft); setCommentMonth(null);}} style={{padding:"7px 14px",border:"none",borderRadius:"6px",background:"#2D7A2D",color:"#fff",cursor:"pointer",fontWeight:"700"}}>保存</button></div></div></div>}
    </div>
  );
}

const LS_KEY = "yojitsu_v1";

function loadLS() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || null; } catch { return null; }
}
function saveLS(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch {}
}

function HeaderInfoPills({editorName, savedAt, savedBy, completeCount, partialCount}) {
  return (
    <div style={{maxWidth:"1600px",margin:"0 auto",padding:"8px 0 12px",display:"flex",gap:"10px",flexWrap:"wrap",alignItems:"center"}}>
      <span style={{fontSize:"11px",color:"#666",background:"#F7FAF7",border:"1px solid #E3EAE3",borderRadius:"999px",padding:"4px 10px",fontFamily:"'Noto Sans JP',sans-serif"}}>現在の更新者: <strong style={{color:"#1A3A1A"}}>{editorName}</strong></span>
      <span style={{fontSize:"11px",color:"#666",background:"#F7FAF7",border:"1px solid #E3EAE3",borderRadius:"999px",padding:"4px 10px",fontFamily:"'Noto Sans JP',sans-serif"}}>最終保存: <strong style={{color:"#1A3A1A"}}>{savedAt || "未保存"}</strong></span>
      <span style={{fontSize:"11px",color:"#666",background:"#F7FAF7",border:"1px solid #E3EAE3",borderRadius:"999px",padding:"4px 10px",fontFamily:"'Noto Sans JP',sans-serif"}}>最終更新者: <strong style={{color:"#1A3A1A"}}>{savedBy || "—"}</strong></span>
      <span style={{fontSize:"11px",color:"#666",background:"#F7FAF7",border:"1px solid #E3EAE3",borderRadius:"999px",padding:"4px 10px",fontFamily:"'Noto Sans JP',sans-serif"}}>月次ステータス: 入力済み <strong style={{color:"#2D7A2D"}}>{completeCount}</strong> / 一部入力 <strong style={{color:"#AA7722"}}>{partialCount}</strong></span>
    </div>
  );
}

export default function App() {
  const ini    = useMemo(()=>makeInitial(getMonths(5)),[]);
  const saved  = useMemo(()=>loadLS(),[]);

  const [startMonth,  setStartMonth]  = useState(saved?.startMonth  ?? 5);
  const [companyName, setCompanyName] = useState(saved?.companyName ?? "Lysis");
  const months = useMemo(()=>getMonths(startMonth),[startMonth]);

  const [revenue,      setRevenue]      = useState(saved?.revenue      ?? ini.revenue);
  const [staff,        setStaff]        = useState(saved?.staff        ?? ini.staff);
  const [expenses,     setExpenses]     = useState(saved?.expenses      ?? ini.expenses);
  const [expenseItems, setExpenseItems] = useState(saved?.expenseItems  ?? DEFAULT_EXPENSE_ITEMS);
  const [expOverride,  setExpOverride]  = useState(saved?.expOverride   ?? {salary:false,bonus:false,transport:false});
  const [actuals,      setActuals]      = useState(() => ({...(saved?.actuals ?? ini.actuals), simple: normalizeSimpleActuals(saved?.actuals?.simple ?? ini.actuals.simple, months)}));
  const [tab,         setTab]         = useState("dashboard");
  const [editorName,  setEditorName]  = useState(saved?.editorName   ?? saved?.savedBy ?? "木原");
  const [savedAt,     setSavedAt]     = useState(saved?.savedAt      ?? null);
  const [savedBy,     setSavedBy]     = useState(saved?.savedBy      ?? null);
  const [dirty,       setDirty]       = useState(false);
  const [expenseMemos,setExpenseMemos]= useState(saved?.expenseMemos ?? {});
  const [taxNote,     setTaxNote]     = useState(saved?.taxNote      ?? "");
  const [diffComments,setDiffComments]= useState(saved?.diffComments ?? {});
  const initialized   = React.useRef(false);

  // 変更検知：初回レンダリング後のみdirtyをtrueにする
  useEffect(()=>{
    if (!initialized.current) { initialized.current = true; return; }
    setDirty(true);
  }, [revenue, staff, expenses, expenseItems, expOverride, actuals, expenseMemos, startMonth, companyName, editorName, taxNote, diffComments]);

  const handleSave = useCallback(() => {
    const now = new Date().toLocaleString("ja-JP");
    saveLS({startMonth, companyName, revenue, staff, expenses, expenseItems, expOverride, actuals, expenseMemos, editorName, taxNote, diffComments, savedAt: now, savedBy: editorName});
    setSavedAt(now);
    setSavedBy(editorName);
    setDirty(false);
  }, [startMonth, companyName, revenue, staff, expenses, expenseItems, expOverride, actuals, expenseMemos, editorName, taxNote, diffComments]);

  const handleChangeStartMonth = (sm) => {
    const hasActuals = actuals && (
      Object.keys(actuals.revItems||{}).length > 0 ||
      Object.values(actuals.items||{}).some(mv=>Object.values(mv).some(v=>v!=="")) ||
      (actuals.simple?.revItems||[]).some(item=>Object.values(item.monthly||{}).some(v=>v!=="")) ||
      (actuals.simple?.expItems||[]).some(item=>Object.values(item.monthly||{}).some(v=>v!=="")) ||
      Object.values(actuals.simple?.rev||{}).some(v=>v!=="") ||
      Object.values(actuals.simple?.exp||{}).some(v=>v!=="")
    );
    if (hasActuals && !window.confirm("会計期間を変更すると入力済みの実績データがリセットされます。\nよろしいですか？")) return;
    const nm = getMonths(sm);
    setStartMonth(sm);
    setRevenue(prev=>prev.map(r=>({...r,monthly:Object.fromEntries(nm.map(m=>[m,r.monthly[m]??0]))})));
    setStaff(prev=>prev.map(s=>({...s,
      salary:   Object.fromEntries(nm.map(m=>[m,s.salary[m]??0])),
      bonus:    Object.fromEntries(nm.map(m=>[m,s.bonus[m]??0])),
      transport:Object.fromEntries(nm.map(m=>[m,s.transport[m]??0])),
    })));
    setExpenses(prev=>Object.fromEntries(Object.entries(prev).map(([id,mv])=>[id,Object.fromEntries(nm.map(m=>[m,mv[m]??0]))])));
    setActuals({revItems:{}, items:Object.fromEntries(expenseItems.map(e=>[e.id,mkMap(nm,"")])), simple:normalizeSimpleActuals({revItems:[{id:makeId("simpleRev"), name:"売上合計", monthly:mkMap(nm,"")}], expItems:[{id:makeId("simpleExp"), name:"費用合計", monthly:mkMap(nm,"")}]}, nm)});
    setExpenseMemos({});
  };

  const filledCount = months.filter((_,i)=>{
    const m = months[i];
    const hasRevDetail = revenue.some(r=>(actuals.revItems?.[r.id]?.[m]??"")!=="");
    const hasRevSimple = (actuals.simple?.revItems||[]).some(item=>(item.monthly?.[m]??"")!=="") || (actuals.simple?.rev?.[m]??"")!=="";
    const hasExpDetail = expenseItems.some(item=>(actuals.items?.[item.id]?.[m]??"")!=="");
    const hasExpSimple = (actuals.simple?.expItems||[]).some(item=>(item.monthly?.[m]??"")!=="") || (actuals.simple?.exp?.[m]??"")!=="";
    return hasRevDetail || hasRevSimple || hasExpDetail || hasExpSimple;
  }).length;
  const fiscalLabel  = `${ALL_MONTHS[startMonth-1]}〜${ALL_MONTHS[(startMonth+10)%12]}期`;
  const headerMonthStatus = useMemo(() => months.map((m)=>{
    const hasRevDetail = revenue.some(r=>(actuals.revItems?.[r.id]?.[m]??"")!=="");
    const hasRevSimple = (actuals.simple?.revItems||[]).some(item=>(item.monthly?.[m]??"")!=="") || (actuals.simple?.rev?.[m]??"")!=="";
    const hasExpDetail = expenseItems.some(item=>(actuals.items?.[item.id]?.[m]??"")!=="");
    const hasExpSimple = (actuals.simple?.expItems||[]).some(item=>(item.monthly?.[m]??"")!=="") || (actuals.simple?.exp?.[m]??"")!=="";
    return { month:m, status:getMonthStatusLabel(hasRevDetail || hasRevSimple, hasExpDetail || hasExpSimple) };
  }), [months, revenue, actuals, expenseItems]);
  const partialCount = headerMonthStatus.filter(x=>x.status.label === "一部入力").length;
  const completeCount = headerMonthStatus.filter(x=>x.status.label === "入力済み").length;

  return (
    <div style={{minHeight:"100vh",background:"#F4F7F4",fontFamily:"'Noto Sans JP','Hiragino Sans',sans-serif"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #E8EDE8",padding:"0 24px",position:"relative",zIndex:100,overflow:"visible"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingTop:"16px",paddingBottom:"8px",maxWidth:"1600px",margin:"0 auto"}}>
          <div style={{display:"flex",alignItems:"baseline",gap:"12px"}}>
            <span style={{fontSize:"18px",fontWeight:"800",color:"#1A3A1A",letterSpacing:"-0.02em"}}>予実管理</span>
            <span style={{fontSize:"12px",color:"#888",background:"#F0F4F0",padding:"2px 8px",borderRadius:"999px"}}>{fiscalLabel}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"12px",flexWrap:"wrap",justifyContent:"flex-end"}}>
            <label style={{display:"flex",alignItems:"center",gap:"6px",fontSize:"11px",color:"#666",fontFamily:"'Noto Sans JP',sans-serif"}}>
              <span>更新者</span>
              <select value={editorName} onChange={e=>setEditorName(e.target.value)} style={{padding:"5px 8px",border:"1px solid #D0D8D0",borderRadius:"6px",fontSize:"12px",background:"#fff",color:"#1A3A1A",fontFamily:"'Noto Sans JP',sans-serif"}}>
                {USER_OPTIONS.map(name => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
            {savedAt && !dirty && <span style={{fontSize:"11px",color:"#aaa",fontFamily:"'Noto Sans JP',sans-serif"}}>💾 {savedAt} 保存済</span>}
            {dirty && <span style={{fontSize:"11px",color:"#AA7722",fontFamily:"'Noto Sans JP',sans-serif",fontWeight:"700"}}>● 未保存</span>}
            <button onClick={handleSave} style={{padding:"6px 14px",background:dirty?"#2D7A2D":"#E8F4E8",color:dirty?"#fff":"#2D7A2D",border:"1px solid #2D7A2D",borderRadius:"6px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",fontWeight:"700",transition:"all 0.15s"}}>
              保存
            </button>
            <div style={{fontSize:"16px",color:"#1A3A1A",fontWeight:"800",fontFamily:"'Noto Sans JP',sans-serif",letterSpacing:"-0.02em"}}>{companyName}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:"2px",maxWidth:"1600px",margin:"0 auto",overflowX:"visible",position:"relative",zIndex:100}}>
          <NavTab label="ダッシュボード" tabId="dashboard" active={tab==="dashboard"} onClick={()=>setTab("dashboard")} badge={filledCount>0?`${filledCount}月入力済`:null}/>
          <NavTab label="収入設定" tabId="revenue" active={tab==="revenue"} onClick={()=>setTab("revenue")}/>
          <DropdownNavTab
            label="経費設定"
            tabId="expenses"
            active={tab==="expenses"}
            subActive={["salary","transport"].includes(tab)?tab:null}
            onClick={()=>setTab("expenses")}
            onSubClick={(id)=>setTab(id)}
            subItems={[{id:"expenses",label:"経費設定"},{id:"salary",label:"給与・賞与"},{id:"transport",label:"交通費"}]}
          />
          <NavTab label="実績入力" tabId="actuals" active={tab==="actuals"} onClick={()=>setTab("actuals")}/>
          <NavTab label="⚙ 設定"  tabId="settings" active={tab==="settings"} onClick={()=>setTab("settings")}/>
        </div>
        <HeaderInfoPills editorName={editorName} savedAt={savedAt} savedBy={savedBy} completeCount={completeCount} partialCount={partialCount} />
      </div>
      {(()=>{
        const PAGE_META = {
          dashboard: {label:"ダッシュボード", sub:"収支サマリ・グラフ",    color:"#1A3A1A", accent:"#2D7A2D", bg:"#E8F4E8"},
          revenue:   {label:"収入設定",       sub:"売上・案件の入力",       color:"#1A3060", accent:"#2255AA", bg:"#E8EEFF"},
          salary:    {label:"給与・賞与設定",  sub:"経費設定 › 給与・賞与", color:"#3A2000", accent:"#AA7722", bg:"#FFF3DC"},
          transport: {label:"交通費設定",      sub:"経費設定 › 交通費",     color:"#3A2000", accent:"#AA7722", bg:"#FFF3DC"},
          expenses:  {label:"経費設定",        sub:"科目ごとの経費入力",    color:"#3A0010", accent:"#C0392B", bg:"#FDECEA"},
          actuals:   {label:"実績入力",        sub:"売上・経費の実績を記録", color:"#1A1A3A", accent:"#553399", bg:"#F0EEFF"},
          settings:  {label:"設定",            sub:"会計期間など",          color:"#2A2A2A", accent:"#666",    bg:"#F0F0F0"},
        };
        const m = PAGE_META[tab] || PAGE_META.dashboard;
        return (
          <div style={{background:m.bg, borderBottom:`3px solid ${m.accent}`, padding:"0 24px"}}>
            <div style={{maxWidth:"1600px", margin:"0 auto", padding:"18px 0 14px"}}>
              <div style={{display:"flex", alignItems:"baseline", gap:"14px"}}>
                <span style={{fontSize:"22px", fontWeight:"900", color:m.color, letterSpacing:"-0.03em", lineHeight:1}}>{m.label}</span>
                <span style={{fontSize:"12px", color:m.accent, fontWeight:"600", opacity:0.8}}>{m.sub}</span>
              </div>
            </div>
          </div>
        );
      })()}
      <div style={{maxWidth:"1600px",margin:"0 auto",padding:"20px 24px"}}>
        {tab==="dashboard" && (
          <Dashboard
            revenue={revenue}
            expenses={expenses}
            expenseItems={expenseItems}
            expOverride={expOverride}
            staff={staff}
            actuals={actuals}
            months={months}
            taxNote={taxNote}
            diffComments={diffComments}
            onChangeDiffComment={(month, text) =>
              setDiffComments(prev => ({ ...prev, [month]: text }))
            }
          />
        )}
        {tab==="revenue"   && <RevenueEditor revenue={revenue} setRevenue={setRevenue} months={months}/>}
        {tab==="salary"    && <SalaryEditor staff={staff} setStaff={setStaff} months={months}/>}
        {tab==="transport" && <TransportEditor staff={staff} setStaff={setStaff} months={months}/>}
        {tab==="expenses"  && <ExpenseEditor expenses={expenses} setExpenses={setExpenses} expenseItems={expenseItems} setExpenseItems={setExpenseItems} expOverride={expOverride} setExpOverride={setExpOverride} staff={staff} months={months} expenseMemos={expenseMemos} setExpenseMemos={setExpenseMemos}/>}
        {tab==="actuals"   && <ActualsEditor actuals={actuals} setActuals={setActuals} revenue={revenue} expenses={expenses} expenseItems={expenseItems} expOverride={expOverride} staff={staff} months={months} taxNote={taxNote} onChangeTaxNote={setTaxNote}/>}
        {tab==="settings"  && <Settings startMonth={startMonth} onChangeStartMonth={handleChangeStartMonth} companyName={companyName} onChangeCompanyName={setCompanyName}/>}
      </div>
    </div>
  );
}
